import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IAiMutualProfile, IConnection, IConnectedMember } from '../interfaces/models/connection';
import { useConnectionsHandlers } from '../services/actions/connections/useConnectionsHandlers';
import { useConnectionsActions } from '../stores/connections/useConnectionsActions';
import { useConnectionsStore } from '../stores/connections/useConnectionsStore';
import { useAuthStore } from '../stores/auth/useAuthStore';
import { useAppStore } from '../stores/application/useAppStore';
import { useAppHandlers } from '../services/actions/app/useAppHandlers';
import { useWorkspaceRequest } from '../services/apis/workspace/useWorkspaceRequest';
import { useAuthHandlers } from '../services/actions/auth/useAuthHandlers';
import momentTimezone from 'moment-timezone';
import styled, { keyframes } from 'styled-components';
import Text from './Text';
import ProfilePhoto from './ProfilePhoto';
import Button from './Button';
import { styles } from '../styles/themes/style';
import LoadingSpinner from './LoadingSpinner';
import AdvancedFilterFragment from '../fragments/AdvancedFilterFragment';
import SvgFragment from '../fragments/SvgFragment';
import TooltipWrapper from './TooltipWrapper';

interface SearchPeopleSduiListProps {
    connection: IConnection;
}

function getProfileUrl(c: IConnection & { linkedinUrl?: string }): string | undefined {
    return (c as any).linkedinUrl ?? (c.publicIdentifier ? `https://www.linkedin.com/in/${c.publicIdentifier}/` : undefined);
}

function getLinkedinInboxComposeUrl(profileId: string, body?: string): string {
    const safeBody = (body ?? '').trim();
    return safeBody
        ? `https://www.linkedin.com/messaging/compose/${profileId}/body/${encodeURIComponent(safeBody)}`
        : `https://www.linkedin.com/messaging/compose/${profileId}/`;
}

function getScoreLabel(score: number): string {
    if (score >= 85) return 'Strongest';
    if (score >= 70) return 'Strong';
    if (score >= 50) return 'Moderate';
    return 'Weak';
}

function getScoreColor(score: number): string {
    if (score >= 85) return '#065f46'; // emerald-800
    if (score >= 70) return '#1d4ed8'; // blue-700
    return '#b45309'; // amber-700
}

function getScoreBg(score: number): string {
    if (score >= 85) return '#ecfdf5'; // emerald-50
    if (score >= 70) return '#eff6ff'; // blue-50
    return '#fffbeb'; // amber-50
}

function getScoreBorder(score: number): string {
    if (score >= 85) return '#a7f3d0'; // emerald-200
    if (score >= 70) return '#bfdbfe'; // blue-200
    return '#fde68a'; // amber-200
}

function parseReasonBullets(reason?: string): string[] {
    if (!reason) return [];
    return reason
        .split(/\s\|\s|\n|•|–|-(?=\s)/)
        .map(s => s.trim())
        .filter(Boolean);
}

function getFullName(firstName?: string, lastName?: string): string {
    return [firstName, lastName]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function getInitials(name: string): string {
    return name
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildLinkedinProfileUrl(publicIdentifier?: string): string | undefined {
    const pi = (publicIdentifier || '').trim();
    if (!pi) return undefined;
    return `https://www.linkedin.com/in/${pi}/`;
}

function buildCompanyFallbackUrl(companyName?: string): string | undefined {
    const name = (companyName || '').trim();
    if (!name) return undefined;
    return `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(name)}`;
}

function extractCompanyMentionsFromMessage(message: string): string[] {
    const text = (message || '').toString();
    if (!text.trim()) return [];

    // Heuristic: catch patterns like "at HTEC Group", "at LeadDelta," etc.
    // Capture up to ~6 words, stop on punctuation/newline.
    const regex = /\bat\s+([A-Z][A-Za-z0-9&.'-]*(?:\s+[A-Z][A-Za-z0-9&.'-]*){0,5})(?=[\s.,;:!?)\]\n]|$)/g;
    const found: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
        const candidate = (m[1] || '').trim().replace(/\s+/g, ' ');
        if (!candidate) continue;
        // Avoid absurdly short/long captures.
        if (candidate.length < 2 || candidate.length > 60) continue;
        found.push(candidate);
    }
    return Array.from(new Set(found));
}

function sanitizeSuggestedMessageHtml(html: string): string {
    // Allow only <a href="...">...</a> and <br>. Everything else becomes text.
    // This defends against unexpected model output.
    try {
        const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const container = doc.body.firstElementChild as HTMLElement | null;
        if (!container) return html;

        const walk = (node: Node) => {
            const children = Array.from(node.childNodes);
            for (const child of children) {
                if (child.nodeType === Node.ELEMENT_NODE) {
                    const el = child as HTMLElement;
                    const tag = el.tagName.toLowerCase();
                    if (tag === 'a') {
                        const href = el.getAttribute('href') || '';
                        // Strip dangerous protocols
                        const safeHref = href.startsWith('http://') || href.startsWith('https://') ? href : '';
                        el.setAttribute('href', safeHref);
                        el.setAttribute('target', '_blank');
                        el.setAttribute('rel', 'noopener noreferrer');
                        // Recurse into its children (text)
                        walk(el);
                    } else if (tag === 'br') {
                        // ok
                    } else {
                        // Replace unsupported element with its text content
                        const text = doc.createTextNode(el.textContent || '');
                        el.replaceWith(text);
                    }
                }
            }
        };

        walk(container);
        return container.innerHTML;
    } catch {
        return html;
    }
}

function htmlTemplateToPlainText(html: string): string {
    // Convert our stored HTML template (mainly <a> and <br>) to plain text for LinkedIn compose URLs.
    // LinkedIn compose URL body expects plain text; otherwise the raw HTML/URLs show up.
    try {
        const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const container = doc.body.firstElementChild as HTMLElement | null;
        if (!container) return (html || '').toString();

        // Replace anchors with their visible text (keep names/company text).
        container.querySelectorAll('a').forEach((a) => {
            const text = doc.createTextNode(a.textContent || '');
            a.replaceWith(text);
        });

        // Convert <br> to newlines.
        container.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));

        // Any remaining tags -> text.
        const out = (container.textContent || '').replace(/\r\n/g, '\n');
        return out;
    } catch {
        // Fallback: best-effort strip anchor tags while keeping inner text.
        return (html || '')
            .toString()
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<a\b[^>]*>/gi, '')
            .replace(/<\/a>/gi, '')
            .replace(/<[^>]+>/g, '');
    }
}

type IntroPathPerson = {
    id: string;
    firstName?: string;
    lastName?: string;
    profilePicture?: string;
    isMe?: boolean;
};

function normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
}

function resolveConnectionByPublicIdentifier(
    items: IConnection[] | undefined,
    publicIdentifier?: string
): IConnection | undefined {
    const wanted = normalize(publicIdentifier);
    if (!wanted || !items?.length) return undefined;
    return items.find((c) => normalize(c.publicIdentifier) === wanted);
}

function findConnectionByPublicIdentifier(
    items: IConnection[] | undefined,
    publicIdentifier?: string
): IConnection | undefined {
    const wanted = normalize(publicIdentifier);
    if (!wanted || !items?.length) return undefined;
    return items.find((c) => normalize(c.publicIdentifier) === wanted && c.profileId);
}

function buildMinimalConnection(wsUser: any): IConnection | undefined {
    const pi = wsUser?.linkedinData?.publicIdentifier;
    if (!pi) return undefined;
    return {
        _id: wsUser._id?.toString() ?? '',
        publicIdentifier: pi,
        firstName: wsUser.linkedinData?.firstName ?? wsUser.firstName ?? '',
        lastName: wsUser.linkedinData?.lastName ?? wsUser.lastName ?? '',
        profilePicture: wsUser.linkedinData?.profilePicture,
        profileId: wsUser.linkedinData?.entityUrn ?? pi,
    } as unknown as IConnection;
}

const AI_LOADING_MESSAGES = [
    'Finding the best paths...',
    'Scoring shared experiences...',
    'Computing relationship signals...',
    'Ranking best introductions...',
    'Almost there...',
];

const LEGACY_LOADING_MESSAGES = [
    'Loading mutual connections...',
    'Preparing results...',
];

type NetworkScopeFilter =
    | 'my_leaddelta'
    | 'my_linkedin'
    | 'workspace_leaddelta'
    | 'workspace_linkedin';

const NETWORK_SCOPE_OPTIONS: Array<{ id: NetworkScopeFilter; label: string }> = [
    { id: 'my_leaddelta', label: 'My LeadDelta network' },
    { id: 'my_linkedin', label: 'My LinkedIn network' },
    { id: 'workspace_leaddelta', label: 'Workspace LeadDelta network' },
    { id: 'workspace_linkedin', label: 'Workspace LinkedIn network' },
];

const NETWORK_SCOPE_TOOLTIPS: Record<NetworkScopeFilter, string> = {
    my_leaddelta: 'All connections in my LeadDelta workspace (including imported and CSV contacts).',
    my_linkedin: 'Only my LinkedIn 1st-degree connections.',
    workspace_leaddelta: 'All connections in this LeadDelta workspace.',
    workspace_linkedin: 'Connections where at least one workspace member is a LinkedIn 1st-degree connection.',
};

const SearchPeopleSduiList: React.FC<SearchPeopleSduiListProps> = ({ connection }) => {
    const {
        messageClickHandler,
        getConnectionByPublicIdentifierHandler,
        getConnectionsByPublicIdentifiersHandler,
        getConnectionAiRecommendationsHandler,
        generateAiRecommendationsHandler
    } = useConnectionsHandlers();
    const { searchPeopleSduiHandler } = useAuthHandlers();
    const { patchConnection } = useConnectionsActions();
    const { store: { connections } } = useConnectionsStore();
    const { store: { workspace, isLoggedToLinkedin, user } } = useAuthStore();
    const { store: { extensionInstalled, extensionVersion } } = useAppStore();
    const { showSuccessToast, showErrorToast, showInfoToast } = useAppHandlers();
    const { getWorkspaceUsersPublicAll } = useWorkspaceRequest();
    const workspaceUsersCacheRef = useRef<any[] | null>(null);
    const lastAiErrorToastConnectionIdRef = useRef<string | null>(null);

    const [legacyConnections, setLegacyConnections] = useState<IConnection[]>([]);
    const [aiProfiles, setAiProfiles] = useState<IAiMutualProfile[]>([]);
    const [aiStatus, setAiStatus] = useState<string>(connection?.aiRecommendationsStatus ?? 'idle');
    const [loadingConversation, setLoadingConversation] = useState<string | null>(null);
    const [noMutualConnections, setNoMutualConnections] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [refreshPathsBusy, setRefreshPathsBusy] = useState(false);

    const [expandedAiProfileId, setExpandedAiProfileId] = useState<string | null>(null);
    const aiCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [editedTemplateByProfileId, setEditedTemplateByProfileId] = useState<Record<string, string>>({});
    const [editingTemplateProfileId, setEditingTemplateProfileId] = useState<string | null>(null);
    const templateTextareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
    const [didAutoExpandStrongest, setDidAutoExpandStrongest] = useState(false);
    const [networkScopeFilter, setNetworkScopeFilter] = useState<NetworkScopeFilter>('workspace_leaddelta');
    const [scoreSortDirection, setScoreSortDirection] = useState<'asc' | 'desc'>('desc');
    const [workspaceMemberCount, setWorkspaceMemberCount] = useState<number | null>(null);

    // AI results member filter (based on `connectedMembers` from BE)
    const [selectedMembersForFilter, setSelectedMembersForFilter] = useState<Array<{ _id: string }>>([{ _id: 'all' }]);

    const connectedMembersUniverse = useMemo(() => {
        const map = new Map<string, IConnectedMember & { _id: string }>();

        (aiProfiles || []).forEach((p) => {
            (p.connectedMembers || []).forEach((cm) => {
                if (!cm?.id) return;
                const memberId = cm.id.toString();
                if (map.has(memberId)) return;
                map.set(memberId, { ...cm, _id: memberId });
            });
        });

        return Array.from(map.values());
    }, [aiProfiles]);

    const isMemberFilterDisabled = networkScopeFilter === 'my_leaddelta' || networkScopeFilter === 'my_linkedin';

    const isAllMembers = selectedMembersForFilter.some(m => m?._id === 'all') || selectedMembersForFilter.length === 0;

    const selectedMemberIds = useMemo(() => {
        if (isAllMembers) return [];
        return selectedMembersForFilter.map(m => m._id).filter(Boolean);
    }, [isAllMembers, selectedMembersForFilter]);

    const selectedMembersTooltip = useMemo(() => {
        if (isAllMembers) return 'All members';
        if (selectedMemberIds.length === 1) {
            const m = connectedMembersUniverse.find((x) => x?._id?.toString() === selectedMemberIds[0]);
            const full = m ? getFullName(m.firstName, m.lastName) : '';
            return full || '1 member selected';
        }
        return `Members: ${selectedMemberIds.length} selected`;
    }, [connectedMembersUniverse, isAllMembers, selectedMemberIds]);

    useEffect(() => {
        if (!isMemberFilterDisabled) return;
        setSelectedMembersForFilter([{ _id: 'all' }]);
    }, [isMemberFilterDisabled]);

    const copyTemplateMessage = async (e: React.MouseEvent<HTMLElement>, message?: string) => {
        e.stopPropagation();
        if (!message?.trim()) return;
        try {
            await navigator.clipboard.writeText(message);
            showSuccessToast({ message: 'Template copied' });
        } catch {
            showErrorToast({ message: 'Failed to copy template' });
        }
    };

    const getTemplateMessageForProfile = (profileId: string, fallback?: string) => {
        return editedTemplateByProfileId[profileId] ?? fallback ?? '';
    };

    const linkifyTemplate = useCallback((
        rawText: string,
        specs: Array<{ text: string; href: string }>
    ): React.ReactNode => {
        const base = rawText || '';
        if (!base.trim() || specs.length === 0) return base;

        // Replace longer matches first to avoid partial overlaps (e.g., "Sergej" vs "Sergej Docic").
        const ordered = [...specs]
            .filter((s) => s?.text?.trim() && s?.href?.trim())
            .sort((a, b) => b.text.length - a.text.length);

        let nodes: Array<string | React.ReactNode> = [base];
        ordered.forEach((spec, specIdx) => {
            const escaped = escapeRegExp(spec.text.trim());
            // For single-word tokens use word-boundary, otherwise match as-is.
            const isSingleWord = !/\s/.test(spec.text.trim());
            const rx = new RegExp(isSingleWord ? `\\b${escaped}\\b` : escaped, 'g');

            const nextNodes: Array<string | React.ReactNode> = [];
            nodes.forEach((node, nodeIdx) => {
                if (typeof node !== 'string') {
                    nextNodes.push(node);
                    return;
                }
                if (!rx.test(node)) {
                    nextNodes.push(node);
                    return;
                }
                rx.lastIndex = 0;
                const parts = node.split(rx);
                const matches = node.match(rx) || [];
                for (let i = 0; i < parts.length; i++) {
                    if (parts[i]) nextNodes.push(parts[i]);
                    if (i < matches.length) {
                        const key = `tpl-link-${specIdx}-${nodeIdx}-${i}`;
                        nextNodes.push(
                            <TemplateLink key={key} href={spec.href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {matches[i]}
                            </TemplateLink>
                        );
                    }
                }
            });
            nodes = nextNodes;
        });

        return <>{nodes}</>;
    }, []);

    const openLinkedinInboxForProfile = async (e: React.MouseEvent<HTMLElement>, profile: IAiMutualProfile) => {
        e.stopPropagation();
        const bodyHtml = getTemplateMessageForProfile(profile?.id?.toString() || '', profile?.suggestedMessage);
        const bodyPlain = htmlTemplateToPlainText(bodyHtml);
        const recipient = await resolveRecommendedConnection(profile);

        if (recipient?.profileId) {
            window.open(getLinkedinInboxComposeUrl(recipient.profileId, bodyPlain), '_blank');
            return;
        }

        const profileUrl = profile?.publicIdentifier ? `https://www.linkedin.com/in/${profile.publicIdentifier}/` : undefined;
        if (profileUrl) window.open(profileUrl, '_blank');
    };

    const resolveRecipientMeta = (profile: IAiMutualProfile) => {
        const recipientId = (profile as any)?.messageRecipientId?.toString();
        const directTarget = resolveConnectionByPublicIdentifier(connections, profile.publicIdentifier);
        const recommendedConnectionId = profile?.id?.toString();
        const directTargetConnectionId = directTarget?._id?.toString();
        const isRecommendedConnectionRecipient =
            !!recipientId &&
            (recipientId === recommendedConnectionId || recipientId === directTargetConnectionId);

        const teamMember = recipientId
            ? (profile.connectedMembers || []).find((member) => member?.id?.toString() === recipientId)
            : undefined;

        // State 3: the candidate itself is a workspace member — recipient IS the candidate.
        const isMemberProfile = !!profile.isMemberProfile;

        return { recipientId, directTarget, isRecommendedConnectionRecipient, teamMember, isMemberProfile };
    };

    const getIntroPathPeople = (profile: IAiMutualProfile): IntroPathPerson[] => {
        const path: IntroPathPerson[] = [];
        const recommendedName = getFullName(profile.firstName, profile.lastName);
        const targetName = getFullName(connection?.firstName, connection?.lastName);
        const { isRecommendedConnectionRecipient, teamMember, isMemberProfile } = resolveRecipientMeta(profile);
        const currentUserId = user?._id?.toString();

        // State 2: a team member is the bridge — add them between Me and Candidate.
        // State 3: the candidate IS the member — skip adding the team member separately
        // (the candidate node below already represents them; avoid duplication).
        // Also: never show the logged-in user in the path (cannot be their own bridge).
        if (!isRecommendedConnectionRecipient && teamMember?.id && !isMemberProfile && teamMember.id.toString() !== currentUserId) {
            path.push({
                id: `tm-${teamMember.id}`,
                firstName: teamMember.firstName,
                lastName: teamMember.lastName,
                profilePicture: teamMember.profilePicture,
            });
        }

        if (recommendedName) {
            path.push({
                id: `rec-${profile.id ?? profile.publicIdentifier ?? 'recommended'}`,
                firstName: profile.firstName,
                lastName: profile.lastName,
                profilePicture: profile.profilePicture,
            });
        }

        if (targetName) {
            path.push({
                id: `target-${connection?._id ?? connection?.publicIdentifier ?? 'target'}`,
                firstName: connection?.firstName,
                lastName: connection?.lastName,
                profilePicture: connection?.profilePicture,
            });
        }

        const deduped: IntroPathPerson[] = [];
        const seen = new Set<string>();
        for (const p of path) {
            if (!p?.id || seen.has(p.id)) continue;
            seen.add(p.id);
            deduped.push(p);
        }
        return deduped;
    };

    const isLinkedInFirstFromConnectedMember = (member?: IConnectedMember): boolean => {
        if (!member) return false;
        const version = normalize(member.version);
        const connectionType = normalize(member.connectionType);
        return version === 'in-1st' || connectionType.includes('1st');
    };

    const isMyLinkedInFirstFromConnection = (conn?: IConnection, userId?: string): boolean => {
        if (!conn || !userId) return false;
        const myEntry = conn.autoTagsArray?.find((entry: any) => entry?._id?.toString() === userId);
        const autoTags = myEntry?.autoTags;
        const myTags = Array.isArray(autoTags) ? autoTags : [];
        return myTags.some((tag: any) => {
            const title = normalize(tag?.title);
            return title === 'linkedin 1st' || title === 'new connection';
        });
    };

    const passesNetworkScopeFilter = useCallback((profile: IAiMutualProfile): boolean => {
        if (networkScopeFilter === 'workspace_leaddelta') return true;

        const currentUserId = user?._id?.toString();
        const conn = resolveConnectionByPublicIdentifier(connections, profile.publicIdentifier);
        const connectedMembers = profile.connectedMembers || [];

        if (networkScopeFilter === 'my_leaddelta') {
            return isMyLinkedInFirstFromConnection(conn, currentUserId);
        }

        if (networkScopeFilter === 'my_linkedin') {
            const inConnectedMembers = connectedMembers.some(
                (member) => member?.id?.toString() === currentUserId && isLinkedInFirstFromConnectedMember(member)
            );
            return inConnectedMembers || isMyLinkedInFirstFromConnection(conn, currentUserId);
        }

        // workspace_linkedin
        return connectedMembers.some((member) => isLinkedInFirstFromConnectedMember(member));
    }, [connections, networkScopeFilter, user?._id]);

    const getMessageRecipientDisplayName = (p: IAiMutualProfile): string => {
        const recipientId = (p as any)?.messageRecipientId?.toString();
        const directTarget = resolveConnectionByPublicIdentifier(connections, p.publicIdentifier);
        const recommendedConnectionId = p?.id?.toString();
        const directTargetConnectionId = directTarget?._id?.toString();
        const isRecommendedRecipient =
            !!recipientId &&
            (recipientId === recommendedConnectionId || recipientId === directTargetConnectionId);

        if (isRecommendedRecipient) {
            return getFullName(p.firstName, p.lastName) || 'Contact';
        }
        if (recipientId) {
            const member = (p.connectedMembers || []).find((m) => m?.id?.toString() === recipientId);
            if (member) {
                const full = getFullName(member.firstName, member.lastName);
                if (full) return full;
            }
            return 'Teammate';
        }
        return getFullName(p.firstName, p.lastName) || 'Contact';
    };

    const fetchWorkspaceUsers = async (): Promise<any[]> => {
        if (workspaceUsersCacheRef.current) return workspaceUsersCacheRef.current;
        try {
            const result = await getWorkspaceUsersPublicAll({ condition: 'or', name: '', email: '', getAll: 'yes' });
            const users = Array.isArray(result) ? result : [];
            workspaceUsersCacheRef.current = users;
            return users;
        } catch {
            return [];
        }
    };

    const resolveWorkspaceMemberConnection = async (memberUserId: string): Promise<IConnection | undefined> => {
        const wsUsers = await fetchWorkspaceUsers();
        const wsUser = wsUsers.find((u: any) => u?._id?.toString() === memberUserId);
        if (!wsUser) return undefined;

        const pi = wsUser?.linkedinData?.publicIdentifier;
        if (pi) {
            const existing = findConnectionByPublicIdentifier(connections, pi);
            if (existing) return existing;
        }

        return buildMinimalConnection(wsUser);
    };

    const resolveRecommendedConnection = async (profile: IAiMutualProfile): Promise<IConnection | undefined> => {
        const fromStore = resolveConnectionByPublicIdentifier(connections, profile.publicIdentifier);
        if (fromStore?.profileId) return fromStore;

        if (profile.publicIdentifier) {
            try {
                const fromApi = await getConnectionByPublicIdentifierHandler(profile.publicIdentifier);
                if (fromApi?.profileId) return fromApi;
            } catch { /* fallback below */ }
        }

        return undefined;
    };

    const handleAiMessageClick = async (
        e: React.MouseEvent<HTMLElement>,
        profile: IAiMutualProfile,
        cardKey: string,
        prefilledMessage?: string
    ) => {
        const { recipientId, isRecommendedConnectionRecipient } = resolveRecipientMeta(profile);

        let targetConnection: IConnection | undefined;
        if (isRecommendedConnectionRecipient || !recipientId) {
            targetConnection = await resolveRecommendedConnection(profile);
        } else {
            targetConnection = await resolveWorkspaceMemberConnection(recipientId);
        }

        if (!targetConnection?.profileId) {
            showErrorToast({ message: 'Unable to find the correct message recipient for this recommendation.' });
            return;
        }

        const safePrefill = prefilledMessage ? htmlTemplateToPlainText(prefilledMessage) : prefilledMessage;
        const setCardLoading = (value: string | null) => setLoadingConversation(value === null ? null : cardKey);
        await messageClickHandler(e, targetConnection, setCardLoading, safePrefill);
    };

    const filteredAiProfiles = useMemo(() => {
        if (!Array.isArray(aiProfiles) || aiProfiles.length === 0) return [];
        const membersFiltered = (isAllMembers || selectedMemberIds.length === 0)
            ? aiProfiles
            : aiProfiles.filter((profile) => {
            const connectedMembers = profile?.connectedMembers || [];
            return connectedMembers.some((cm) => cm?.id && selectedMemberIds.includes(cm.id.toString()));
        });

        const networkFiltered = membersFiltered.filter((profile) => passesNetworkScopeFilter(profile));
        return [...networkFiltered].sort((a, b) => {
            const aScore = a.score ?? 0;
            const bScore = b.score ?? 0;
            return scoreSortDirection === 'asc' ? aScore - bScore : bScore - aScore;
        });
    }, [aiProfiles, isAllMembers, selectedMemberIds, passesNetworkScopeFilter, scoreSortDirection]);

    useEffect(() => {
        if (!Array.isArray(aiProfiles) || aiProfiles.length === 0) {
            setEditedTemplateByProfileId({});
            return;
        }
        setEditedTemplateByProfileId((prev) => {
            const next: Record<string, string> = {};
            aiProfiles.forEach((profile) => {
                const profileId = profile?.id?.toString();
                if (!profileId) return;
                next[profileId] = prev[profileId] ?? (profile.suggestedMessage || '');
            });
            return next;
        });
    }, [aiProfiles]);

    const isAiMode = aiStatus === 'generating' || aiStatus === 'ready' || (connection?.linkedinAiMutualProfilesCount ?? 0) > 0;
    const loadingMessages = isAiMode ? AI_LOADING_MESSAGES : LEGACY_LOADING_MESSAGES;

    const updateTableConnection = useCallback((next: Partial<IConnection>) => {
        if (!connection?._id) return;
        patchConnection(connection._id, next);
    }, [connection?._id, patchConnection]);

    const todayKeyAi = momentTimezone.tz(new Date(), 'America/New_York').format('YYYY-MM-DD');
    const dailyMaxAi = Number(process.env.REACT_APP_AI_RECOMMENDATIONS_DAILY_LIMIT ?? 20) || 20;
    const usedTodayAi = (user as any)?.aiRecommendationsAnalytics?.dailyDate === todayKeyAi
        ? (Number((user as any)?.aiRecommendationsAnalytics?.dailyCount ?? 0) || 0)
        : 0;
    const remainingTodayAi = Math.max(0, dailyMaxAi - usedTodayAi);
    const dailyLimitReachedAi = remainingTodayAi <= 0;
    const dailyLimitReachedMessage = `You\u2019ve reached your daily limit of ${dailyMaxAi} warm intros.`;

    const runWarmIntroRefresh = useCallback(async () => {
        if (!connection?._id) return;
        if (dailyLimitReachedAi) {
            showInfoToast({ message: dailyLimitReachedMessage });
            return;
        }
        setRefreshPathsBusy(true);
        try {
            setError(null);
            setAiProfiles([]);
            setLegacyConnections([]);
            setNoMutualConnections(false);
            setAiStatus('generating');
            updateTableConnection({
                aiRecommendationsStatus: 'generating',
                linkedinAiMutualProfilesCount: 0,
                // Clear any cached AI profiles so we never instantly show stale results on refresh.
                linkedinAiMutualProfiles: []
            } as any);
            showInfoToast({ message: 'AI is analyzing mutual connections. Results will appear shortly.' });

            if (extensionInstalled && isLoggedToLinkedin) {
                try {
                    await searchPeopleSduiHandler(connection.profileId ?? '', connection.publicIdentifier ?? '', connection._id);
                } catch (scanError) {
                    console.warn('Mutual scan failed; generating AI recommendations anyway.', scanError);
                    showInfoToast({ message: 'Mutual scan failed. Generating AI recommendations anyway.' });
                }
            }
            await generateAiRecommendationsHandler(connection._id);
            setLoading(false);
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 429) {
                showErrorToast({ message: 'Daily AI limit reached. Please try again tomorrow.' });
            } else {
                showErrorToast({ message: 'Failed to generate Warm Intro AI recommendations. Please try again.' });
            }
            setAiStatus('error');
            updateTableConnection({
                aiRecommendationsStatus: 'error',
                linkedinAiMutualProfilesCount: 0
            } as any);
            setLoading(false);
        } finally {
            setRefreshPathsBusy(false);
        }
    }, [
        connection,
        dailyLimitReachedAi,
        dailyLimitReachedMessage,
        extensionInstalled,
        generateAiRecommendationsHandler,
        isLoggedToLinkedin,
        searchPeopleSduiHandler,
        showErrorToast,
        showInfoToast,
        updateTableConnection
    ]);

    // Safety: if generation finished (or connection changed), never keep the refresh button disabled
    // due to a stuck local "busy" flag.
    useEffect(() => {
        if (!connection?._id) return;
        if (aiStatus === 'generating') return;
        setRefreshPathsBusy(false);
    }, [aiStatus, connection?._id]);

    useEffect(() => {
        if (!(loading || aiStatus === 'generating')) return;
        const id = setInterval(() => {
            setSlideIndex((i) => (i + 1) % loadingMessages.length);
        }, 2500);
        return () => clearInterval(id);
    }, [loading, aiStatus, loadingMessages.length]);

    // Sync aiStatus from prop when connection changes
    useEffect(() => {
        const initialAiCount = connection?.linkedinAiMutualProfilesCount ?? 0;
        const propStatus = connection?.aiRecommendationsStatus ?? 'idle';
        const nextStatus = initialAiCount > 0 ? 'ready' : propStatus;

        setAiStatus((prev) => {
            // If a new generation starts (e.g. user clicked "Refresh paths" from the table),
            // we MUST transition to "generating" even if we previously showed "ready".
            if (nextStatus === 'generating' && prev !== 'generating') return 'generating';

            // Once we have local truth from polling ("ready"/"error"), don't let
            // stale table row status override it (except the case above).
            if (prev === 'ready' || prev === 'error') return prev;

            // While polling is active, don't allow "generating" to be overwritten
            // to "idle" by a stale table row.
            if (prev === 'generating' && nextStatus === 'idle') return prev;

            return nextStatus;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection?._id, connection?.aiRecommendationsStatus, connection?.linkedinAiMutualProfilesCount]);

    // When generation is triggered externally (e.g. from the table), clear any previously shown results
    // so the sidebar doesn't keep rendering stale data while the new run is in progress.
    useEffect(() => {
        if (!connection?._id) return;
        const nextStatus = connection?.aiRecommendationsStatus ?? 'idle';
        const nextCount = connection?.linkedinAiMutualProfilesCount ?? 0;
        if (nextStatus !== 'generating') return;
        if (nextCount > 0) return;

        setError(null);
        setAiProfiles([]);
        setLegacyConnections([]);
        setNoMutualConnections(false);
        setLoading(true);
        setDidAutoExpandStrongest(false);
        setExpandedAiProfileId(null);
    }, [connection?._id, connection?.aiRecommendationsStatus, connection?.linkedinAiMutualProfilesCount]);

    // Reset accordion state when switching connections
    useEffect(() => {
        setExpandedAiProfileId(null);
        setEditingTemplateProfileId(null);
        setDidAutoExpandStrongest(false);
        lastAiErrorToastConnectionIdRef.current = null;
    }, [connection?._id]);

    useEffect(() => {
        let cancelled = false;
        if (!workspace?._id) {
            setWorkspaceMemberCount(null);
            return;
        }
        (async () => {
            try {
                const result = await getWorkspaceUsersPublicAll({ condition: 'or', name: '', email: '', getAll: 'yes' });
                const users = Array.isArray(result) ? result : [];
                if (cancelled) return;
                setWorkspaceMemberCount(users.length);
                if (!workspaceUsersCacheRef.current && users.length) {
                    workspaceUsersCacheRef.current = users;
                }
            } catch {
                if (!cancelled) setWorkspaceMemberCount(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getWorkspaceUsersPublicAll stable from useWorkspaceRequest
    }, [workspace?._id]);

    // If AI generation fails (often due to provider outage / temporary unavailability),
    // surface a clear toast so the user understands why they see "Try again".
    useEffect(() => {
        const id = connection?._id?.toString?.() ? connection._id.toString() : (connection?._id as any);
        if (!id) return;
        if (aiStatus !== 'error') return;
        if (lastAiErrorToastConnectionIdRef.current === id) return;
        lastAiErrorToastConnectionIdRef.current = id;

        showErrorToast({
            message: 'Warm Intro AI is temporarily unavailable. Please try again in a few minutes.'
        });
    }, [aiStatus, connection?._id, showErrorToast]);

    // Reset member filter when we load a new set of AI recommendations.
    // Important: don't depend only on `aiProfiles.length`, because the list can be replaced
    // with the same count and then the dropdown pre-checks will be wrong.
    useEffect(() => {

        if (!connection?._id) return;
        if (aiStatus !== 'ready') return;
        if (!Array.isArray(aiProfiles) || aiProfiles.length === 0) return;
        setSelectedMembersForFilter([{ _id: 'all' }]);
    }, [connection?._id, aiStatus, aiProfiles]);

    // Auto-expand strongest card when sidebar opens / data loads.
    // Only do this when we haven't expanded anything yet (so we don't override user toggles).
    useEffect(() => {
        if (!connection?._id) return;
        if (aiStatus !== 'ready') return;
        if (didAutoExpandStrongest) return;
        if (expandedAiProfileId) return;
        if (!Array.isArray(filteredAiProfiles) || filteredAiProfiles.length === 0) return;

        const strongest = filteredAiProfiles[0];
        const key = (strongest?.id ?? '0')?.toString?.() ? (strongest.id ?? '0').toString() : String(strongest?.id ?? '0');
        setExpandedAiProfileId(key);
        setDidAutoExpandStrongest(true);
    }, [connection?._id, aiStatus, filteredAiProfiles, expandedAiProfileId, didAutoExpandStrongest]);

    // Policy: whenever filters change, ALWAYS expand the first card in the filtered list.
    // This matches the requirement that "the first card is always open" regardless of scope filter.
    const lastAiFilterSignatureRef = useRef<string>('');
    useEffect(() => {
        if (!connection?._id) return;
        if (aiStatus !== 'ready') return;
        if (!Array.isArray(filteredAiProfiles) || filteredAiProfiles.length === 0) {
            setExpandedAiProfileId(null);
            return;
        }

        const signature = [
            networkScopeFilter,
            scoreSortDirection,
            // selectedMemberIds is stable via useMemo; include to treat member filter changes as filter changes
            selectedMemberIds.join('|') || 'all',
        ].join('::');

        if (lastAiFilterSignatureRef.current === signature) return;
        lastAiFilterSignatureRef.current = signature;

        const first = filteredAiProfiles[0];
        const firstKeyRaw = (first as any)?.id ?? '0';
        const firstKey = firstKeyRaw?.toString?.() ? firstKeyRaw.toString() : String(firstKeyRaw);
        setExpandedAiProfileId(firstKey);
    }, [connection?._id, aiStatus, filteredAiProfiles, networkScopeFilter, scoreSortDirection, selectedMemberIds]);

    // UX: when expanding a card, scroll it to the top of the sidebar content.
    useEffect(() => {
        if (!expandedAiProfileId) return;
        const el = aiCardRefs.current[expandedAiProfileId];
        if (!el) return;
        // Next tick to allow layout to expand before scrolling.
        setTimeout(() => {
            try {
                el.scrollIntoView({ block: 'start', behavior: 'smooth' });
            } catch {
                // older browsers / polyfills
                el.scrollIntoView(true);
            }
        }, 0);
    }, [expandedAiProfileId]);

    // Poll while generating
    useEffect(() => {
        if (aiStatus !== 'generating') {
            return;
        }
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const pollOnce = async () => {
            try {
                if (cancelled) return;
                const result: any = await getConnectionAiRecommendationsHandler(connection._id);

                // Only stop polling on terminal states. Treating "idle" as terminal can freeze
                // the sidebar at "0 results" while backend generation is still warming up.
                if (result?.status && (result.status === 'ready' || result.status === 'error')) {
                    setAiStatus(result.status);
                    if (Array.isArray(result.linkedinAiMutualProfiles)) {
                        setAiProfiles(result.linkedinAiMutualProfiles);
                    }
                    updateTableConnection({
                        aiRecommendationsStatus: result.status,
                        linkedinAiMutualProfilesCount: result.linkedinAiMutualProfilesCount ?? (Array.isArray(result.linkedinAiMutualProfiles) ? result.linkedinAiMutualProfiles.length : 0)
                    } as any);
                    setLoading(false);
                    return;
                }
            } catch {
                // ignore and retry
            }

            if (!cancelled) {
                timeoutId = setTimeout(pollOnce, 5000);
            }
        };

        timeoutId = setTimeout(pollOnce, 2000);

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiStatus, connection._id]);

    // Initial data load
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setNoMutualConnections(false);
        setLegacyConnections([]);
        setAiProfiles([]);

        (async () => {
            try {
                // Always fetch latest AI status once, because the sidebar opens using row data
                // that might still contain stale `aiRecommendationsStatus`.
                // If this component was opened with localStatus 'generating'
                // (e.g. user clicked "Refresh paths" from the table), the POST
                // to the backend may not have been sent yet. The GET call below
                // could therefore return the OLD 'ready' state + stale profiles.
                // In that case we must trust the local prop and stay in
                // 'generating' mode so polling picks up new data later.
                const localStatus = connection?.aiRecommendationsStatus;
                if (localStatus === 'generating') {
                    if (cancelled) return;
                    setAiStatus('generating');
                    setAiProfiles([]);
                    setLoading(false);
                    return;
                }

                const aiResult: any = await getConnectionAiRecommendationsHandler(connection._id);
                const apiStatus = aiResult?.status;
                const freshStatus = apiStatus ?? localStatus ?? 'idle';
                const freshAiProfiles = Array.isArray(aiResult?.linkedinAiMutualProfiles)
                    ? aiResult.linkedinAiMutualProfiles
                    : [];

                if (freshStatus === 'generating') {
                    if (cancelled) return;
                    setAiStatus('generating');
                    setAiProfiles([]);
                    setLoading(false);
                    return;
                }

                if (freshStatus === 'error') {
                    if (cancelled) return;
                    setAiStatus('error');
                    updateTableConnection({
                        aiRecommendationsStatus: 'error',
                        linkedinAiMutualProfilesCount: 0
                    } as any);
                    setLoading(false);
                    return;
                }

                if (freshStatus === 'ready') {
                    if (cancelled) return;
                    setAiProfiles(Array.isArray(freshAiProfiles) ? freshAiProfiles : []);
                    setAiStatus('ready');
                    updateTableConnection({
                        aiRecommendationsStatus: 'ready',
                        linkedinAiMutualProfilesCount: aiResult?.linkedinAiMutualProfilesCount ?? freshAiProfiles.length
                    } as any);
                    setLoading(false);
                    return;
                }

                // If AI is idle, fall back to legacy mutual connections flow.
                const publicIdentifier = connection?.publicIdentifier ?? '';
                const profileId = connection?.profileId ?? '';
                if (!profileId || !publicIdentifier) {
                    setError('No profile ID or public identifier');
                    setLoading(false);
                    return;
                }

                const storedMutualProfiles = Array.isArray(connection?.linkedinMutualProfiles)
                    ? Array.from(new Set(connection.linkedinMutualProfiles
                        .map((id) => (typeof id === 'string' ? id.trim() : ''))
                        .filter(Boolean)))
                    : [];

                if (storedMutualProfiles.length > 0) {
                    const storedConnections: any = await getConnectionsByPublicIdentifiersHandler(storedMutualProfiles);
                    if (cancelled) return;
                    const cachedList = Array.isArray(storedConnections)
                        ? storedConnections
                        : Array.isArray(storedConnections?.connections)
                            ? storedConnections.connections
                            : [];
                    setLegacyConnections(cachedList);
                    setNoMutualConnections(cachedList.length === 0);
                    setLoading(false);
                    return;
                }

                if (cancelled) return;
                setNoMutualConnections(true);
                setLoading(false);
            } catch (e) {
                if (!cancelled) {
                    setError((e as Error)?.message ?? 'Failed to load');
                    setLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection?._id, connection?.profileId, connection?.linkedinMutualProfiles, connection?.linkedinAiMutualProfilesCount, connection?.aiRecommendationsStatus]);

    const profileName =
        getFullName(connection?.firstName, connection?.lastName) ||
        (connection?.name ? connection.name.replace(/\s+/g, ' ').trim() : '') ||
        'this profile';

    const targetHeadline =
        (connection?.headline && String(connection.headline).trim()) ||
        (connection?.jobTitle && String(connection.jobTitle).trim()) ||
        '';

    const renderWarmIntroTargetCard = (options: { showRefresh?: boolean }) => {
        const showRefresh = options.showRefresh !== false;
        const name =
            getFullName(connection?.firstName, connection?.lastName) ||
            (connection?.name ? connection.name.replace(/\s+/g, ' ').trim() : '') ||
            'Unknown';
        const initials = getInitials(name);

        const btn = (
            <WarmIntroRefreshPathsBtn
                type="button"
                disabled={dailyLimitReachedAi || refreshPathsBusy}
                onClick={(e) => {
                    e.stopPropagation();
                    void runWarmIntroRefresh();
                }}
            >
                <SvgFragment type="reload" />
                <span>Refresh paths</span>
            </WarmIntroRefreshPathsBtn>
        );

        const refreshControl = showRefresh
            ? dailyLimitReachedAi ? (
                <TooltipWrapper tooltipText={dailyLimitReachedMessage}>
                    <span style={{ display: 'inline-flex', flexShrink: 0 }}>{btn}</span>
                </TooltipWrapper>
            ) : (
                btn
            )
            : null;

        return (
            <WarmIntroProfileCard>
                <ProfilePhoto
                    source={connection?.profilePicture}
                    capitalLetters={initials}
                    height="40px"
                    width="40px"
                    tooltipMessage={name}
                />
                <WarmIntroProfileCardText>
                    <WarmIntroProfileTextStack>
                        <Text $bold $ellipsis="1" style={{ color: '#333951' }}>
                            {name}
                        </Text>
                        {targetHeadline ? (
                            <Text
                                $ellipsis="1"
                                $lighter
                                $paragraph3
                                showTooltip
                                style={{ color: styles.colors.black400 }}
                            >
                                {targetHeadline}
                            </Text>
                        ) : null}
                    </WarmIntroProfileTextStack>
                    {refreshControl}
                </WarmIntroProfileCardText>
            </WarmIntroProfileCard>
        );
    };

    if (loading) {
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                    <WarmIntroSummaryLine style={{ color: styles.colors.black400 }}>
                        Loading intro paths for <strong>{profileName}</strong>…
                    </WarmIntroSummaryLine>
                    {renderWarmIntroTargetCard({ showRefresh: false })}
                    <LoadingSlider>
                        {LEGACY_LOADING_MESSAGES.map((msg, i) => (
                            <SliderText key={msg} $active={i === slideIndex}>
                                {msg}
                            </SliderText>
                        ))}
                    </LoadingSlider>
                </TitleBlock>
                <SpinnerWrap>
                    <LoadingSpinner height="40px" width="40px" color={styles.colors.primary600} />
                </SpinnerWrap>
            </ListWrap>
        );
    }

    if (aiStatus === 'generating') {
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                    <LoadingSlider>
                        {AI_LOADING_MESSAGES.map((msg, i) => (
                            <SliderText key={msg} $active={i === slideIndex}>
                                {msg}
                            </SliderText>
                        ))}
                    </LoadingSlider>
                    {renderWarmIntroTargetCard({ showRefresh: false })}
                </TitleBlock>
                <SpinnerWrap>
                    <AiSpinnerWrap>
                        <LoadingSpinner height="36px" width="36px" color={styles.colors.primary600} />
                        <Text $lighter>Finding paths...</Text>
                    </AiSpinnerWrap>
                </SpinnerWrap>
            </ListWrap>
        );
    }

    if (aiStatus === 'error') {
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                    <WarmIntroSummaryLine style={{ color: styles.colors.red600 }}>
                        Something went wrong generating recommendations. Please try again.
                    </WarmIntroSummaryLine>
                    {renderWarmIntroTargetCard({ showRefresh: true })}
                </TitleBlock>
            </ListWrap>
        );
    }

    if (error) {
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                </TitleBlock>
                <Placeholder $error>
                    <Text $red>{error}</Text>
                </Placeholder>
            </ListWrap>
        );
    }

    // AI results view
    if (aiProfiles.length > 0) {
        const shownCount = filteredAiProfiles.length;
        const hideWorkspaceSidebarFilters = workspaceMemberCount === 1;
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                    <WarmIntroSummaryLine>
                        There are <strong>{shownCount}</strong>{' '}
                        {shownCount === 1 ? 'path' : 'paths'} to <strong>{profileName}</strong>
                    </WarmIntroSummaryLine>
                    {renderWarmIntroTargetCard({ showRefresh: true })}
                </TitleBlock>

                <AiFilterRow $onlySort={hideWorkspaceSidebarFilters}>
                {!hideWorkspaceSidebarFilters && (
                <>
                <AiFilterControl>
                        <AdvancedFilterFragment
                            drop="right"
                            tabs={[]}
                            upgradePlan={false}
                            infoTitle="Filter recommendations by network scope."
                            inputTitle="Workspace LeadDelta network"
                            hideResetButton={true}
                            panelWidth="328px"
                            showSearchField={false}
                            tooltipMessage={NETWORK_SCOPE_OPTIONS.find((opt) => opt.id === networkScopeFilter)?.label || 'Workspace LeadDelta network'}
                            customSingleValue={
                                <FilterSingleValueWithArrow>
                                    <span className="label">{NETWORK_SCOPE_OPTIONS.find((opt) => opt.id === networkScopeFilter)?.label || 'Workspace LeadDelta network'}</span>
                                </FilterSingleValueWithArrow>
                            }
                            filterOptions={NETWORK_SCOPE_OPTIONS.map((opt) => ({
                                value: opt.label,
                                label: (
                                    <NetworkScopeOptionLabel>
                                        <span className="text">{opt.label}</span>
                                        <TooltipWrapper tooltipText={NETWORK_SCOPE_TOOLTIPS[opt.id]}>
                                            <span
                                                className="info"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <SvgFragment type="info" />
                                            </span>
                                        </TooltipWrapper>
                                    </NetworkScopeOptionLabel>
                                ),
                                id: opt.id,
                                selected: false,
                                object: { _id: opt.id }
                            }))}
                            selectedItems={[{ _id: networkScopeFilter }]}
                            onSelectedItemHandler={(items: any[], clickedItem?: any) => {
                                const next = (clickedItem?.id || items?.[0]?._id) as NetworkScopeFilter | undefined;
                                if (!next) return;
                                setNetworkScopeFilter(next);
                            }}
                            onResetSelectionsHandler={() => setNetworkScopeFilter('workspace_leaddelta')}
                        />
                    </AiFilterControl>
                    <AiFilterControl>
                        <AdvancedFilterFragment
                            drop="right"
                            tabs={[]}
                            upgradePlan={false}
                            infoTitle="Filter AI mutual profiles by workspace member who has this connection."
                            inputTitle="All members"
                            hideResetButton={true}
                            panelWidth="248px"
                            disabled={isMemberFilterDisabled}
                            tooltipMessage={selectedMembersTooltip}
                            customSingleValue={
                                <FilterSingleValueWithArrow>
                                    <span className="label">
                                        {isAllMembers ? 'All members' : `Members: ${selectedMemberIds.length} selected`}
                                    </span>
                                </FilterSingleValueWithArrow>
                            }
                            filterOptions={[
                                {
                                    value: 'all',
                                    label: 'All members',
                                    id: 'all',
                                    selected: false,
                                    object: { _id: 'all' }
                                },
                                ...connectedMembersUniverse.map((m) => {
                                    const fullName = getFullName(m.firstName, m.lastName) || m._id;
                                    return {
                                        value: fullName,
                                        label: (
                                            <MemberOptionLabel>
                                                <ProfilePhoto
                                                    source={m.profilePicture}
                                                    capitalLetters={getInitials(fullName)}
                                                    height="24px"
                                                    width="24px"
                                                    tooltipMessage={fullName}
                                                />
                                                <span>{fullName}</span>
                                            </MemberOptionLabel>
                                        ),
                                        id: m._id,
                                        selected: false,
                                        object: m
                                    };
                                })
                            ]}
                            selectedItems={selectedMembersForFilter}
                            onSelectedItemHandler={(items: any[], clickedItem?: any) => {
                                if (isMemberFilterDisabled) return;
                                const safe = Array.isArray(items) ? items : [];
                                const getId = (it: any) => it?._id ?? it?.id ?? null;

                                const ids = safe.map(getId).filter(Boolean);
                                const hasAll = ids.includes('all');
                                const memberIds = ids.filter((id: any) => id && id !== 'all');

                                const clickedId = clickedItem?.id ?? null;

                                if (clickedId === 'all') {
                                    if (hasAll) {
                                        setSelectedMembersForFilter([{ _id: 'all' }]);
                                    } else if (!memberIds.length) {
                                        setSelectedMembersForFilter([{ _id: 'all' }]);
                                    } else {
                                        setSelectedMembersForFilter(memberIds.map((id: string) => ({ _id: id })));
                                    }
                                } else {
                                    if (!memberIds.length) {
                                        setSelectedMembersForFilter([{ _id: 'all' }]);
                                    } else {
                                        setSelectedMembersForFilter(memberIds.map((id: string) => ({ _id: id })));
                                    }
                                }

                            }}
                            onResetSelectionsHandler={() => setSelectedMembersForFilter([{ _id: 'all' }])}
                        />
                    </AiFilterControl>
                </>
                )}


                    <TooltipWrapper tooltipText={scoreSortDirection === 'asc' ? 'Lowest to Strongest' : 'Strongest to Lowest'}>
                        <SortButton
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setScoreSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                            }}
                            aria-label={scoreSortDirection === 'asc' ? 'Lowest to Strongest' : 'Strongest to Lowest'}
                        >
                            <SvgFragment type='sortVertical' />
                        </SortButton>
                    </TooltipWrapper>
                </AiFilterRow>

                {shownCount === 0 ? (
                    <Placeholder>
                        <Text $lighter>No recommended introductions for the selected member(s).</Text>
                    </Placeholder>
                ) : (
                <AiList key={selectedMemberIds.length ? selectedMemberIds.join('|') : 'all'}>
                    {filteredAiProfiles.map((profile, index) => {
                        const cardKey = (profile.id ?? String(index))?.toString?.()
                            ? (profile.id ?? String(index)).toString()
                            : String(profile.id ?? String(index));
                        const isExpanded = expandedAiProfileId === cardKey;

                        const name = getFullName(profile.firstName, profile.lastName) || 'Unknown';
                        const profileUrl = profile.publicIdentifier ? `https://www.linkedin.com/in/${profile.publicIdentifier}/` : undefined;
                        const score = profile.score ?? 0;
                        const bullets = parseReasonBullets(profile.reason);
                        const introPathPeople = getIntroPathPeople(profile);

                        // Resolve card state for header decoration
                        const { teamMember: cardTeamMember, isMemberProfile: cardIsMemberProfile, directTarget: cardDirectTarget } = resolveRecipientMeta(profile);
                        const headerHeadline = profile?.headline || profile?.jobTitle || cardDirectTarget?.headline || cardDirectTarget?.jobTitle;

                        // const connectedMembers = profile?.connectedMembers || [];
                        // Meta "connected members" must follow the same rule as the actual profile filtering:
                        // - if nothing is selected => show all connected members
                        // - otherwise => show only connected members that match selectedMemberIds
                        // const connectedMembersToShow = selectedMemberIds.length === 0
                        //     ? connectedMembers
                        //     : connectedMembers.filter((cm) => cm?.id && selectedMemberIds.includes(cm.id.toString()));
                        // const visibleConnected = connectedMembersToShow.slice(0, 4);
                        // const remainingCount = Math.max(0, connectedMembersToShow.length - visibleConnected.length);

                        return (
                            <AiCard
                                key={cardKey}
                                ref={(el) => { aiCardRefs.current[cardKey] = el; }}
                                $index={index}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                onClick={() => setExpandedAiProfileId(prev => (prev === cardKey ? null : cardKey))}
                                onKeyDown={(e) => {
                                    const target = e.target as HTMLElement;
                                    const isTypingElement =
                                        target?.tagName === 'TEXTAREA' ||
                                        target?.tagName === 'INPUT' ||
                                        target?.isContentEditable;
                                    if (isTypingElement) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setExpandedAiProfileId(prev => (prev === cardKey ? null : cardKey));
                                    }
                                }}
                            >
                                <AiCardHeader>
                                    <AiCardLeft>
                                        <ProfilePhoto
                                            source={profile.profilePicture}
                                            capitalLetters={name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                                            height="40px"
                                            width="40px"
                                            minWidth='40px'
                                            minHeight='40px'
                                            tooltipMessage={name}
                                        />
                                        <AiCardInfo>
                                            <AiNameRow>
                                                {profileUrl ? (
                                                    <AiProfileLink href={profileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                        <Text $bold>{name}</Text>
                                                    </AiProfileLink>
                                                ) : (
                                                    <Text $bold>{name}</Text>
                                                )}
                                                {/* State 3: candidate IS the workspace member */}
                                                {cardIsMemberProfile && (
                                                    <AiMemberBadge>Member</AiMemberBadge>
                                                )}
                                            </AiNameRow>
                                            {headerHeadline && (
                                                <TooltipWrapper tooltipText={headerHeadline}>
                                                    <span style={{ display: 'block', minWidth: 0 }}>
                                                        <Text $ellipsis="1" $lighter $paragraph3>{headerHeadline}</Text>
                                                    </span>
                                                </TooltipWrapper>
                                            )}
                                        </AiCardInfo>
                                    </AiCardLeft>
                                    <AiCardHeaderRight>
                                        <ScoreBadge $color={getScoreColor(score)} $bg={getScoreBg(score)} $border={getScoreBorder(score)}>
                                            {getScoreLabel(score)}
                                        </ScoreBadge>
                                        <ExpandIconWrap
                                            $isExpanded={isExpanded}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setExpandedAiProfileId(prev => (prev === cardKey ? null : cardKey));
                                            }}
                                            aria-label={isExpanded ? 'Collapse card' : 'Expand card'}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setExpandedAiProfileId(prev => (prev === cardKey ? null : cardKey));
                                                }
                                            }}
                                        >
                                            <SvgFragment type="arrowDown" />
                                        </ExpandIconWrap>
                                    </AiCardHeaderRight>
                                </AiCardHeader>
                                <ScoreBarRow>
                                    <ScoreBar>
                                        <ScoreBarFill $pct={score} $color={getScoreColor(score)} />
                                    </ScoreBar>
                                    <ScorePercent>{Math.round(score)}%</ScorePercent>
                                </ScoreBarRow>

                                {isExpanded && (
                                    <>
                                        <AiSectionBox>
                                            {/* State 2: a workspace member is the bridge — show member row at top, separated by a divider */}
                                            {!cardIsMemberProfile && cardTeamMember && (
                                                <>
                                                    <AiMemberRow>
                                                        <ProfilePhoto
                                                            source={cardTeamMember.profilePicture}
                                                            capitalLetters={(getFullName(cardTeamMember.firstName, cardTeamMember.lastName) || '?')
                                                                .split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                                                            height="20px"
                                                            width="20px"
                                                            tooltipMessage={getFullName(cardTeamMember.firstName, cardTeamMember.lastName)}
                                                        />
                                                        <AiMemberRowName>
                                                            {getFullName(cardTeamMember.firstName, cardTeamMember.lastName)}
                                                        </AiMemberRowName>
                                                        <AiMemberBadge>Member</AiMemberBadge>
                                                    </AiMemberRow>
                                                    <AiMemberDivider />
                                                </>
                                            )}

                                            {introPathPeople.length >= 3 && (
                                                <IntroPathInner>
                                                    <IntroPathRow>
                                                        {introPathPeople.map((person, i) => {
                                                            const fullName = getFullName(person.firstName, person.lastName) || 'Unknown';
                                                            return (
                                                                <React.Fragment key={person.id}>
                                                                    <IntroPathNode>
                                                                        <ProfilePhoto
                                                                            source={person.profilePicture}
                                                                            capitalLetters={getInitials(fullName)}
                                                                            height="32px"
                                                                            width="32px"
                                                                            tooltipMessage={fullName}
                                                                        />
                                                                        <TooltipWrapper tooltipText={fullName}>
                                                                            <IntroPathName $bold={i === 1}>{fullName}</IntroPathName>
                                                                        </TooltipWrapper>
                                                                        {person.isMe && <IntroPathSub>(me)</IntroPathSub>}
                                                                    </IntroPathNode>
                                                                    {i < introPathPeople.length - 1 && (
                                                                        <IntroPathArrow aria-hidden>
                                                                            <SvgFragment type="warmIntroPathArrow" />
                                                                        </IntroPathArrow>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </IntroPathRow>
                                                </IntroPathInner>
                                            )}

                                            <AiRelationshipSection>
                                                <AiRelationshipTitle>
                                                    <span>{name.split(' ')[0]} ↔ {profileName.split(' ')[0]} relationship highlights</span>
                                                </AiRelationshipTitle>

                                                {selectedMemberIds.length === 0
                                                    ? (bullets.length > 0 ? (
                                                        <BulletList>
                                                            {bullets.map((b, i) => (
                                                                <BulletItem key={i}>
                                                                    <SparkleIconSmall>✦</SparkleIconSmall>
                                                                    <BulletText>{b}</BulletText>
                                                                </BulletItem>
                                                            ))}
                                                        </BulletList>
                                                    ) : profile.reason ? (
                                                        <ReasonText>{profile.reason}</ReasonText>
                                                    ) : null)
                                                    : null}
                                            </AiRelationshipSection>
                                        </AiSectionBox>

                                        <AiTemplateSection>
                                            <AiTemplateHeader>
                                                <AiTemplateHeaderLeft>
                                                    <span>Template</span>
                                                </AiTemplateHeaderLeft>
                                                <AiTemplateHeaderActions>
                                                    <TemplateIconButton
                                                        as="button"
                                                        type="button"
                                                        onClick={(e) => openLinkedinInboxForProfile(e, profile)}
                                                        aria-label="Open LinkedIn inbox"
                                                    >
                                                        <SvgFragment type="linkedin" />
                                                    </TemplateIconButton>
                                                    <TemplateIconButton
                                                        as="button"
                                                        type="button"
                                                        $primary
                                                        onClick={(e) => copyTemplateMessage(e, getTemplateMessageForProfile(cardKey, profile.suggestedMessage))}
                                                        aria-label="Copy template message"
                                                    >
                                                        <SvgFragment type="copy" />
                                                    </TemplateIconButton>
                                                </AiTemplateHeaderActions>
                                            </AiTemplateHeader>
                                            <AiSectionBox>
                                                <TemplateBody>
                                                    {editingTemplateProfileId === cardKey ? (
                                                        <TemplateTextarea
                                                            ref={(el) => { templateTextareaRefs.current[cardKey] = el; }}
                                                            value={htmlTemplateToPlainText(getTemplateMessageForProfile(cardKey, profile.suggestedMessage))}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                            onBlur={() => setEditingTemplateProfileId(null)}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setEditedTemplateByProfileId((prev) => ({ ...prev, [cardKey]: value }));
                                                            }}
                                                            placeholder="No suggested message."
                                                        />
                                                    ) : (
                                                        <TemplateRendered
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingTemplateProfileId(cardKey);
                                                                setTimeout(() => templateTextareaRefs.current[cardKey]?.focus(), 0);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setEditingTemplateProfileId(cardKey);
                                                                    setTimeout(() => templateTextareaRefs.current[cardKey]?.focus(), 0);
                                                                }
                                                            }}
                                                        >
                                                            {(() => {
                                                                const msg = getTemplateMessageForProfile(cardKey, profile.suggestedMessage);
                                                                const hasAnchor = msg.includes('<a ') && msg.includes('</a>');
                                                                if (hasAnchor) {
                                                                    return <span dangerouslySetInnerHTML={{ __html: sanitizeSuggestedMessageHtml(msg).replace(/\n/g, '<br/>') }} />;
                                                                }
                                                                return linkifyTemplate(
                                                                    msg,
                                                                    [
                                                                    // Candidate (recipient) links
                                                                    ...(profile.publicIdentifier
                                                                        ? [
                                                                            { text: getFullName(profile.firstName, profile.lastName), href: buildLinkedinProfileUrl(profile.publicIdentifier) as string },
                                                                            { text: (profile.firstName || '').trim(), href: buildLinkedinProfileUrl(profile.publicIdentifier) as string },
                                                                        ].filter((x) => x.text)
                                                                        : []),

                                                                    // Target links
                                                                    ...(connection?.publicIdentifier
                                                                        ? [
                                                                            { text: getFullName(connection?.firstName, connection?.lastName), href: buildLinkedinProfileUrl(connection.publicIdentifier) as string },
                                                                            { text: (connection?.firstName || '').trim(), href: buildLinkedinProfileUrl(connection.publicIdentifier) as string },
                                                                        ].filter((x) => x.text)
                                                                        : []),

                                                                    // Company links (fallback to LinkedIn company search)
                                                                    ...(() => {
                                                                        const msg = getTemplateMessageForProfile(cardKey, profile.suggestedMessage);
                                                                        const extractedCompanies = extractCompanyMentionsFromMessage(msg);
                                                                        const explicitCompanies = [connection?.company].filter(Boolean) as string[];
                                                                        const allCompanies = Array.from(new Set([...explicitCompanies, ...extractedCompanies]));

                                                                        const candidateFull = getFullName(profile.firstName, profile.lastName);
                                                                        const targetFull = getFullName(connection?.firstName, connection?.lastName);
                                                                        const blocked = new Set(
                                                                            [candidateFull, targetFull, (profile.firstName || '').trim(), (connection?.firstName || '').trim()]
                                                                                .filter(Boolean)
                                                                                .map((s) => s.toLowerCase())
                                                                        );

                                                                        return allCompanies
                                                                            .filter((cName) => !!cName && !blocked.has(cName.toLowerCase()))
                                                                            .map((cName) => {
                                                                                const href = buildCompanyFallbackUrl(cName);
                                                                                return href ? { text: cName, href } : null;
                                                                            })
                                                                            .filter(Boolean) as Array<{ text: string; href: string }>;
                                                                    })(),
                                                                ]
                                                                );
                                                            })()}
                                                        </TemplateRendered>
                                                    )}
                                                </TemplateBody>
                                            </AiSectionBox>
                                        </AiTemplateSection>

                                        <AiMessageDisclaimer>
                                            AI can make mistakes. Please review important details before reaching out.
                                        </AiMessageDisclaimer>

                                        <AiMessageButtonRow onClick={(e) => e.stopPropagation()}>
                                            {(() => {
                                                const isDisabled =
                                                    !!workspace?.isStarter ||
                                                    !extensionInstalled ||
                                                    !isLoggedToLinkedin ||
                                                    extensionVersion !== process.env.REACT_APP_VERSION ||
                                                    !profile.publicIdentifier ||
                                                    !!loadingConversation;

                                                const tooltipText = workspace?.isStarter
                                                    ? 'Upgrade to Pro to unlock this feature'
                                                    : !extensionInstalled
                                                        ? 'Please install the extension'
                                                        : extensionVersion !== process.env.REACT_APP_VERSION
                                                            ? 'Update extension'
                                                            : !isLoggedToLinkedin
                                                                ? 'Login to Linkedin'
                                                                : !profile.publicIdentifier
                                                                    ? 'This profile cannot be messaged right now.'
                                                                    : (loadingConversation && loadingConversation !== cardKey)
                                                                        ? 'Please wait for the current conversation to load.'
                                                                        : '';

                                                const btn = (
                                                    <Button
                                                        $type="blue"
                                                        $height="36px"
                                                        disabled={isDisabled}
                                                        tooltipMessage={tooltipText}
                                                        onClickHandler={(e: React.MouseEvent<HTMLElement>) =>
                                                            handleAiMessageClick(
                                                                e,
                                                                profile,
                                                                cardKey,
                                                                getTemplateMessageForProfile(cardKey, profile.suggestedMessage) || undefined
                                                            )
                                                        }
                                                        isLoading={loadingConversation === cardKey}
                                                    >
                                                        {`Message ${getMessageRecipientDisplayName(profile).split(' ')[0] || 'Contact'}`}
                                                    </Button>
                                                );

                                                // Disabled buttons often don't trigger hover; wrap with TooltipWrapper to ensure tooltip shows.
                                                if (!isDisabled || !tooltipText) return btn;

                                                return (
                                                    <TooltipWrapper tooltipText={tooltipText}>
                                                        <span style={{ display: 'flex', width: '100%' }}>
                                                            {btn}
                                                        </span>
                                                    </TooltipWrapper>
                                                );
                                            })()}
                                        </AiMessageButtonRow>
                                    </>
                                )}
                            </AiCard>
                        );
                    })}
                </AiList>
                )}
            </ListWrap>
        );
    }

    // Legacy mutual connections view
    if (legacyConnections.length === 0) {
        return (
            <ListWrap>
                <TitleBlock>
                    <WarmIntroHeaderTopRow>
                        <WarmIntroHeaderLeft>
                            <SparkleIcon aria-hidden>✦</SparkleIcon>
                            <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderLeft>
                    </WarmIntroHeaderTopRow>
                    <WarmIntroSummaryLine>
                        {noMutualConnections ? (
                            <>
                                There are <strong>0</strong> mutual connections to <strong>{profileName}</strong>
                            </>
                        ) : (
                            <>No profiles to show for <strong>{profileName}</strong></>
                        )}
                    </WarmIntroSummaryLine>
                    {noMutualConnections ? (
                        <Text $lighter style={{ fontSize: '14px', lineHeight: '20px' }}>
                            Sorry, you have no mutual connections with this person.
                        </Text>
                    ) : null}
                    {renderWarmIntroTargetCard({ showRefresh: true })}
                </TitleBlock>
            </ListWrap>
        );
    }

    const count = legacyConnections.length;

    return (
        <ListWrap>
            <TitleBlock>
                <WarmIntroHeaderTopRow>
                    <WarmIntroHeaderLeft>
                        <SparkleIcon aria-hidden>✦</SparkleIcon>
                        <WarmIntroBrandTitle>Warm Intro AI</WarmIntroBrandTitle>
                        <BetaTag>BETA</BetaTag>
                    </WarmIntroHeaderLeft>
                </WarmIntroHeaderTopRow>
                <WarmIntroSummaryLine>
                    There are <strong>{count}</strong>{' '}
                    {count === 1 ? 'mutual connection' : 'mutual connections'} to <strong>{profileName}</strong>
                </WarmIntroSummaryLine>
                {renderWarmIntroTargetCard({ showRefresh: true })}
            </TitleBlock>
            <List>
                {legacyConnections.map((c, index) => {
                    const name =
                        getFullName(c.firstName, c.lastName) ||
                        (c.name ? c.name.replace(/\s+/g, ' ').trim() : '') ||
                        'Unknown';
                    const profileUrl = getProfileUrl(c);
                    const isMessageDisabled = workspace?.isStarter || !extensionInstalled || !isLoggedToLinkedin || extensionVersion !== process.env.REACT_APP_VERSION;
                    return (
                        <Item key={c._id} $index={index}>
                            {profileUrl ? (
                                <ItemLink href={profileUrl} target="_blank" rel="noopener noreferrer">
                                    <ProfilePhoto source={c.profilePicture} capitalLetters={name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()} height="40px" width="40px" tooltipMessage={name} />
                                    <ItemContent>
                                        <Text $bold>{name}</Text>
                                        {c.headline && <Text $ellipsis="1" $lighter $paragraph3>{c.headline}</Text>}
                                    </ItemContent>
                                </ItemLink>
                            ) : (
                                <ItemTextWrap>
                                    <ProfilePhoto source={c.profilePicture} capitalLetters={name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()} height="40px" width="40px" tooltipMessage={name} />
                                    <ItemContent>
                                        <Text $bold>{name}</Text>
                                        {c.headline && <Text $ellipsis="1" $lighter $paragraph3>{c.headline}</Text>}
                                    </ItemContent>
                                </ItemTextWrap>
                            )}
                            <MessageButtonWrap hoverColor={styles.colors.primary100} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                                <Button
                                    transparent
                                    $smallButton
                                    disabled={isMessageDisabled || !!loadingConversation}
                                    tooltipMessage={workspace?.isStarter ? 'Upgrade to Pro to unlock this feature' : (loadingConversation && loadingConversation !== c._id) ? 'Please wait for the current conversation to load.' : 'Message'}
                                    onClickHandler={(e: React.MouseEvent<HTMLElement>) => messageClickHandler(e, c, setLoadingConversation)}
                                    $SVGtype="message"
                                    isLoading={loadingConversation === c._id}
                                />
                            </MessageButtonWrap>
                        </Item>
                    );
                })}
            </List>
        </ListWrap>
    );
};

// ── Animations ──────────────────────────────────────────────────────────────

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ── Legacy list styles ───────────────────────────────────────────────────────

const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

const Item = styled.li<{ $index?: number }>`
    display: flex;
    animation: ${slideIn} 0.35s ease forwards;
    animation-delay: ${({ $index = 0 }) => `${$index * 0.05}s`};
    opacity: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: ${styles.colors.black100};
    border-radius: 6px;
    &:hover { background: ${styles.colors.primary100}; }
`;

const ItemLink = styled.a`
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    color: inherit;
    border-radius: 6px;
    transition: filter 0.2s ease;
    &:hover { opacity: 100; background: ${styles.colors.primary100}; }
`;

const ItemContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const ItemTextWrap = styled.div`
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const MessageButtonWrap = styled.div<{ hoverColor?: string }>`
    flex-shrink: 0;
    padding-right: 8px;
    &:hover { background: ${({ hoverColor }) => hoverColor ?? styles.colors.primary100}; }
`;

// ── AI card styles ───────────────────────────────────────────────────────────

const AiFilterRow = styled.div<{ $onlySort?: boolean }>`
    width: 100%;
    align-items: center;
    gap: 8px;
    ${({ $onlySort }) =>
        $onlySort
            ? `
        display: flex;
        justify-content: flex-end;
    `
            : `
        display: grid;
        grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.75fr) 34px;
    `}
`;

const AiFilterControl = styled.div`
    min-width: 0;
    width: 100%;
    & > * {
        width: 100%;
    }
`;

const FilterSingleValueWithArrow = styled.span`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: ${styles.colors.black500 ?? '#4a5568'};
    font-size: 14px;
    line-height: 20px;
    .label {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 6px;
    }
    .svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

const SortButton = styled.button`
    width: 34px;    
    height: 34px;
    box-sizing: border-box;
    border: none;
    box-shadow: 0 0 0 2px #DAE0E8;
    border-radius: 4px;
    background: ${styles.colors.white ?? '#fff'};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: start;
    margin-left: -2px;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    overflow: hidden;
    & .svg {
        display: block;
    }
    &:hover {
        box-shadow: 0 0 0 2px ${styles.colors.primary600};
        & .svg path { stroke: ${styles.colors.primary600}; }
    }
`;

const MemberOptionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    & span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const NetworkScopeOptionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;

    .text {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .info {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        cursor: default;
    }

    .info .svg {
        width: 14px;
        height: 14px;
        display: block;
    }
`;


const IntroPathRow = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    overflow: hidden;
`;

const IntroPathNode = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 56px;
    text-align: center;
    flex-shrink: 0;
`;

const IntroPathArrow = styled.span`
    width: 16px;
    height: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    margin-top: 9px;
    flex-shrink: 0;
    .path {
        stroke: #bcc8fa;
        stroke-width: 1.5;
        stroke-linecap: round;
        stroke-linejoin: round;
        fill: none;
    }
`;

const IntroPathName = styled.span<{ $bold?: boolean }>`
    margin-top: 4px;
    font-size: 10px;
    line-height: 12px;
    color: ${styles.colors.black500 ?? '#4a5568'};
    font-weight: ${({ $bold }) => ($bold ? 700 : 400)};
    white-space: nowrap;
    max-width: 64px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const IntroPathSub = styled.span`
    font-size: 9px;
    line-height: 11px;
    color: ${styles.colors.black300 ?? '#8c96ad'};
`;

const AiSectionBox = styled.div`
    width: 100%;
    border: 1px solid ${styles.colors.black200 ?? '#e5e7eb'};
    border-radius: 4px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
`;

const AiTemplateSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const AiTemplateHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-family: Nunito Sans;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    font-weight: 700;
    color: ${styles.colors.black500 ?? '#374151'};
`;

const AiTemplateHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const AiTemplateHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TemplateIconButton = styled.a<{ $primary?: boolean }>`
    border: none;
    background: transparent;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 4px;
    text-decoration: none;
    &:hover {
        background: ${styles.colors.primary100};
    }
    ${({ $primary }) => $primary && `
        svg path {
            stroke: ${styles.colors.primary600} !important;
        }
    `}
`;

const TemplateBody = styled.div`
    font-size: 13px;
    line-height: 1.45;
    color: ${styles.colors.black400 ?? '#6b7280'};
    padding: 0;
    white-space: pre-wrap;
`;

const TemplateTextarea = styled.textarea`
    width: 100%;
    min-height: 96px;
    border: none;
    outline: none;
    resize: vertical;
    background: transparent;
    font-family: Nunito Sans;
    font-size: 13px;
    line-height: 1.45;
    color: ${styles.colors.black400 ?? '#6b7280'};
`;

const TemplateLink = styled.a`
    color: ${styles.colors.primary600};
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

// Ensure backend-provided <a> tags match the same styling.
const TemplateRendered = styled.div`
    width: 100%;
    min-height: 96px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: Nunito Sans;
    font-size: 13px;
    line-height: 1.45;
    color: ${styles.colors.black400 ?? '#6b7280'};
    cursor: text;
    a {
        color: ${styles.colors.primary600};
        text-decoration: none;
    }
    a:hover { text-decoration: underline; }
`;

const AiList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

const AiCard = styled.div<{ $index?: number }>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    border-radius: 4px;
    border: 1px solid ${styles.colors.black200 ?? '#e5e7eb'};
    background: #fff;
    cursor: pointer;
    animation: ${fadeIn} 0.4s ease forwards;
    animation-delay: ${({ $index = 0 }) => `${$index * 0.08}s`};
    opacity: 0;
    transition: box-shadow 0.2s ease;
    &:hover {
        box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    }
`;

const AiCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
`;

const AiCardHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AiMemberRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
`;

const AiMemberDivider = styled.hr`
    border: none;
    border-top: 1px solid ${styles.colors.black200 ?? '#e5e7eb'};
    margin: 0px;
`;

const IntroPathInner = styled.div`
    width: 100%;
    margin-bottom: 8px;
`;

const AiMemberRowName = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${styles.colors.black600};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AiMemberBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 53px;
    height: 15px;
    border-radius: 4px;
    gap: 4px;
    padding: 0 4px;
    background: #EBEFF4;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0px;
    color: #545E77;
    white-space: nowrap;
    box-sizing: border-box;
    flex-shrink: 0;
`;

const AiCardLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
`;

const AiCardInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const AiNameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    > * { min-width: 0; }
`;

const AiProfileLink = styled.a`
    text-decoration: none;
    color: inherit;
    &:hover span { text-decoration: underline; }
`;

const ScoreBadge = styled.span<{ $color: string; $bg: string; $border: string }>`
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 9999px;
    border: 1px solid ${({ $border }) => $border};
        color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
    white-space: nowrap;
`;

const ExpandIconWrap = styled.span<{ $isExpanded?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    .svg {
        transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(360deg)')};
        transition: transform 0.2s ease;
    }
    &:focus { outline: none; }
`;

const AiRelationshipSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const AiRelationshipTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-family: Nunito Sans;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    font-weight: 600;
    color: ${styles.colors.black500 ?? '#374151'};
`;

const BulletList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const BulletItem = styled.li`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 6px;
    background: ${styles.colors.primary100};
    border-radius: 4px;
    padding-top: 6px;
    padding-bottom: 6px;
    padding-right: 8px;
    padding-left: 8px;
    font-size: 13px;
    color: ${styles.colors.black400 ?? '#6b7280'};
    line-height: 1.5;
`;

const BulletText = styled.span`
    display: block;
    flex: 1;
    min-width: 0;
    text-align: left;
`;

const ReasonText = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${styles.colors.black400 ?? '#6b7280'};
    line-height: 1.5;
`;

const ScoreBarRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const ScoreBar = styled.div`
    flex: 1;
    height: 3px;
    background: ${styles.colors.black200 ?? '#e5e7eb'};
    border-radius: 2px;
    overflow: hidden;
`;

const ScorePercent = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${styles.colors.black400 ?? '#6b7280'};
    flex-shrink: 0;
`;

const ScoreBarFill = styled.div<{ $pct: number; $color: string }>`
    height: 100%;
    width: ${({ $pct }) => `${$pct}%`};
    background: ${({ $color }) => $color};
    border-radius: 2px;
    transition: width 0.6s ease;
`;

const AiMessageDisclaimer = styled.p`
    margin: 0px 0 2px;
    padding: 0;
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 11px;
    line-height: 20px;
    letter-spacing: 0;
    color: ${styles.colors.black300};
`;

const AiMessageButtonRow = styled.div`
    margin-top: 0;
`;

// Match Header.tsx "BETA" pill styling
const BetaTag = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 12px;
    width: 25px;
    font-size: 8px;
    background: ${styles.colors.primary600};
    border-radius: 3px;
    color: ${styles.colors.white};
    flex-shrink: 0;
    position: relative;
    top: -4px;
`;

const SparkleIcon = styled.span`
    font-size: 16px;
    color: ${styles.colors.primary600};
    line-height: 1;
`;

const WarmIntroHeaderTopRow = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding-right: 36px;
    box-sizing: border-box;
`;

const WarmIntroHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const WarmIntroBrandTitle = styled.span`
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    color: #333951;
`;

const WarmIntroSummaryLine = styled.p`
    margin: 0;
    width: 100%;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #333951;

    strong {
        font-weight: 700;
    }
`;

const WarmIntroProfileCard = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    padding: 12px;
    background: ${styles.colors.primary100};
    border-radius: 8px;
    box-sizing: border-box;
`;

const WarmIntroProfileCardText = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
`;

const WarmIntroProfileTextStack = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const WarmIntroRefreshPathsBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-shrink: 0;
    border: none;
    margin: 0;
    cursor: pointer;
    box-sizing: border-box;
    padding: 4px 0;
    background: transparent;
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0;
    color: ${styles.colors.primary600};

    span {
        color: inherit;
        white-space: nowrap;
    }

    .svg {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        display: block;
    }

    .svg path {
        fill: ${styles.colors.primary600} !important;
    }

    &:hover:not(:disabled) {
        color: ${styles.colors.primary500};

        .svg path {
            fill: ${styles.colors.primary500} !important;
        }
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const SparkleIconSmall = styled.span`
    font-size: 12px;
    color: ${styles.colors.primary600};
    flex-shrink: 0;
`;

const AiSpinnerWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: center;
`;

// ── Shared styles ────────────────────────────────────────────────────────────

const Placeholder = styled.div<{ $error?: boolean }>`
    padding: 16px;
    color: ${({ $error }) => ($error ? styles.colors.red600 : styles.colors.black300)};
    font-size: 14px;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const SpinnerWrap = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
    width: 100%;
`;

const LoadingSlider = styled.div`
    position: relative;
    min-height: 24px;
    width: 100%;
    text-align: left;
`;

const SliderText = styled.span<{ $active?: boolean }>`
    position: absolute;
    left: 0;
    width: 100%;
    color: ${styles.colors.black400};
    font-size: 14px;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: translateY(${({ $active }) => ($active ? 0 : 8)}px);
    text-align: left;
    transition: opacity 0.4s ease, transform 0.4s ease;
`;

const ListWrap = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
`;

export default SearchPeopleSduiList;
